import React from 'react';

import {ActionIcon, Menu, Select} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import {IconPlus, IconTrash} from '@tabler/icons-react';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {getLanguageDisplayName} from 'src/app/training/languageUtils';

export interface LanguageManagerProps {
  availableLanguages: string[];
  activeLanguage: string;
  defaultLanguage: string;
  unusedLanguages: {value: string; label: string}[];
  onActiveLanguageChange: (code: string) => void;
  onAddLanguage: (code: string) => void;
  onRemoveLanguage: (code: string) => void;
}

export const LanguageManager: React.FC<LanguageManagerProps> = ({
  availableLanguages,
  activeLanguage,
  defaultLanguage,
  unusedLanguages,
  onActiveLanguageChange,
  onAddLanguage,
  onRemoveLanguage,
}) => {
  const [confirmDeleteOpen, confirmDeleteHandlers] = useDisclosure(false);

  function handleDeleteConfirm(): void {
    confirmDeleteHandlers.close();
    onRemoveLanguage(activeLanguage);
  }

  return (
    <>
      <Group gap="sm" align="flex-end" wrap="nowrap">
        <Select
          label="Editing"
          data={availableLanguages.map(code => ({
            value: code,
            label: `${getLanguageDisplayName(code)}${
              code === defaultLanguage ? ' (Default)' : ''
            }`,
          }))}
          value={activeLanguage}
          onChange={val => val && onActiveLanguageChange(val)}
          allowDeselect={false}
          style={{flex: 1}}
        />
        <Menu shadow="md" width={200}>
          <Menu.Target>
            <ActionIcon variant="light" size="lg" aria-label="Add language">
              <IconPlus size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Add Language</Menu.Label>
            {unusedLanguages.slice(0, 20).map(lang => (
              <Menu.Item
                key={lang.value}
                onClick={() => onAddLanguage(lang.value)}>
                {lang.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
        {activeLanguage !== defaultLanguage && (
          <ActionIcon
            variant="light"
            color="red"
            size="lg"
            aria-label="Remove language"
            onClick={confirmDeleteHandlers.open}>
            <IconTrash size={16} />
          </ActionIcon>
        )}
      </Group>
      <Text size="xs" c="dimmed" mt="xs">
        {activeLanguage === defaultLanguage
          ? 'The default language is shown when no other match is available and cannot be removed.'
          : 'Trainees see this language when it matches their preference.'}
      </Text>
      <ConfirmDialog
        opened={confirmDeleteOpen}
        title="Remove Language"
        body={`Are you sure you want to remove ${getLanguageDisplayName(
          activeLanguage
        )}? All content for this language will be removed.`}
        buttonTextYes="Yes, I'm sure"
        onClose={confirmDeleteHandlers.close}
        onConfirm={handleDeleteConfirm}
        size="md"
      />
    </>
  );
};
